import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import cx from 'clsx';

import styles from './styles.module.scss';

export type OptionValue = {
  value: string;
  label: string;
  className?: string;
};

export type DropdownProps = {
  options: Array<OptionValue>;
  onChange: (item: OptionValue) => void;
  label: ReactElement;
  className?: string;
  placeHolder?: string;
  disabled: boolean;
};

export const Dropdown: FC<DropdownProps> = ({
  options,
  onChange,
  label,
  className,
  placeHolder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { addEventListener, removeEventListener } = document;

  const handleClickOutside = (e: MouseEvent) => {
    const { current } = dropdownRef;
    const { target } = e;

    if (isOpen && current && !current.contains(target as HTMLDivElement)) {
      setIsOpen(false);
      //unbind the listener which is no longer required until the dropdown is not open
      removeEventListener('mousedown', handleClickOutside);
    }
  };

  const onOptionSelection = (option: OptionValue) => {
    onChange(option);
    setIsOpen(false);
  };

  useEffect(() => {
    // Bind the event listener
    const addListener = addEventListener.bind(document);
    isOpen && addListener('mousedown', handleClickOutside);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      //unbind event listener
      const removeListener = removeEventListener.bind(document);
      removeListener('mousedown', handleClickOutside);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-testid="dropDownMenu"
      className={cx(`${isOpen && styles.open}`, styles.wrapper, className)}
      ref={dropdownRef}
    >
      <label className={styles.placeholder}>{placeHolder}</label>
      <button
        type="button"
        data-testid="toggleButton"
        className={styles.header}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeHolder}
        disabled={disabled}
      >
        {label}
      </button>

      <ul className={styles.listContainer}>
        {options.map((option: OptionValue, i: number) => (
          <li
            data-testid={`option${i}`}
            key={option.value}
            className={styles.listItem}
          >
            <button
              type="button"
              data-testid={`optionButton${i}`}
              onClick={() => onOptionSelection(option)}
              className={cx(styles.btnStyling, option.className)}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
